import React, { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { format } from 'date-fns'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { sendEmail } from '@/lib/email'
import { buildMeetingIcs, icsToBase64 } from '@/lib/ics'
import { X, Video } from 'lucide-react'
import { toast } from 'sonner'
import { toLocalInput, isUrl, type MeetingRow, type MeetingEntityType } from '@/lib/meetings'

/**
 * MeetingModal — create/edit a sales_meeting. Shared by MeetingsThread (deal
 * sidebar, entity-linked) and SalesCalendar (standalone or any linkage).
 * entityType/entityId are used only on CREATE; edit takes linkage from the row.
 * defaultStart prefills the start field for create-from-calendar (a day click).
 */

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
  workspaceId: string
  ownerId: string | null
  entityType?: MeetingEntityType | null
  entityId?: string | null
  editing?: MeetingRow | null
  defaultStart?: string | null   // datetime-local value
}

const field: React.CSSProperties = {
  padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)',
  borderRadius: 8, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', width: '100%',
}
const lbl: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase',
  letterSpacing: '0.06em', display: 'block', marginBottom: 5,
}

// Reminder offset (minutes before start). 'custom' preserves a stored remind_at
// that doesn't match a preset; 'none' = no reminder.
const OFFSET_MIN: Record<string, number> = { '15': 15, '60': 60, '1440': 1440 }

// Unguessable room slug — 16 random bytes -> base64url (~128 bits), matching
// Meetings CP1's server-side recipe (encode(gen_random_bytes(16),'base64') with
// +/ -> -_). The meeting-guest-token fn validates this slug for guest video.
export function generateRoomSlug(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const meetUrl = (slug: string) => `${window.location.origin}/meet/${slug}`
const REMINDER_OPTS: Array<{ v: string; label: string }> = [
  { v: 'none', label: 'None' },
  { v: '15', label: '15 minutes before' },
  { v: '60', label: '1 hour before' },
  { v: '1440', label: '1 day before' },
]

export default function MeetingModal({ open, onClose, onSaved, workspaceId, ownerId, entityType = null, entityId = null, editing = null, defaultStart = null }: Props) {
  const { workspace, user } = useAuth()
  const [title, setTitle] = useState('')
  const [startLocal, setStartLocal] = useState('')
  const [endLocal, setEndLocal] = useState('')
  const [location, setLocation] = useState('')
  const [attendees, setAttendees] = useState('')
  const [notes, setNotes] = useState('')
  const [reminderOpt, setReminderOpt] = useState<string>('60')   // default: 1 hour before
  const [customRemindAt, setCustomRemindAt] = useState<string | null>(null)  // preserved stored value for 'custom'
  const [sendInvite, setSendInvite] = useState(true)   // default on; the checkbox only shows when attendees present
  const [roomToken, setRoomToken] = useState<string | null>(null)   // Meetings CP3: guest video slug

  // Hydrate whenever the modal opens (edit -> row values; create -> blank +
  // optional prefilled start).
  useEffect(() => {
    if (!open) return
    if (editing) {
      setTitle(editing.title)
      setStartLocal(toLocalInput(editing.start_time))
      setEndLocal(toLocalInput(editing.end_time))
      setLocation(editing.location ?? '')
      setAttendees((editing.attendee_emails ?? []).join(', '))
      setNotes(editing.notes ?? '')
      // Derive the reminder option from the stored remind_at vs start_time; a
      // non-preset offset shows 'Custom' and preserves the stored value verbatim.
      if (!editing.remind_at) { setReminderOpt('none'); setCustomRemindAt(null) }
      else {
        const diffMin = Math.round((new Date(editing.start_time).getTime() - new Date(editing.remind_at).getTime()) / 60000)
        const preset = Object.keys(OFFSET_MIN).find(k => OFFSET_MIN[k] === diffMin)
        if (preset) { setReminderOpt(preset); setCustomRemindAt(null) }
        else { setReminderOpt('custom'); setCustomRemindAt(editing.remind_at) }
      }
      setRoomToken(editing.meeting_room_token ?? null)
    } else {
      setTitle(''); setStartLocal(defaultStart ?? ''); setEndLocal(''); setLocation(''); setAttendees(''); setNotes('')
      setReminderOpt('60'); setCustomRemindAt(null)
      setRoomToken(null)
    }
    setSendInvite(true)
  }, [open, editing, defaultStart])

  const save = useMutation({
    mutationFn: async () => {
      if (!workspaceId) throw new Error('Not signed in')
      if (!title.trim()) throw new Error('Title is required')
      if (!startLocal || !endLocal) throw new Error('Start and end times are required')
      const startIso = new Date(startLocal).toISOString()
      const endIso = new Date(endLocal).toISOString()
      if (new Date(endIso) <= new Date(startIso)) throw new Error('End must be after start')
      // Reminder: a preset offset computes remind_at from start; 'custom' keeps
      // the stored value verbatim; 'none' clears it.
      let remindIso: string | null = null
      if (reminderOpt === 'custom') remindIso = customRemindAt
      else if (OFFSET_MIN[reminderOpt] != null) remindIso = new Date(new Date(startIso).getTime() - OFFSET_MIN[reminderOpt] * 60000).toISOString()
      const payload = {
        title: title.trim(),
        start_time: startIso,
        end_time: endIso,
        location: location.trim() || null,
        attendee_emails: attendees.split(',').map(s => s.trim()).filter(Boolean),
        notes: notes.trim() || null,
        remind_at: remindIso,
        meeting_room_token: roomToken,
      }
      const attendeeList = payload.attendee_emails
      let meetingId: string
      if (editing) {
        // Re-arm: a rescheduled meeting whose reminder now lands in the future
        // should fire again — clear the reminded_at dedup marker.
        const upd = (remindIso && new Date(remindIso) > new Date()) ? { ...payload, reminded_at: null } : payload
        const { error } = await (supabase as any).from('sales_meetings').update(upd).eq('id', editing.id)
        if (error) throw error
        meetingId = editing.id
      } else {
        const { data, error } = await (supabase as any).from('sales_meetings').insert({
          workspace_id: workspaceId,
          entity_type: entityType,
          entity_id: entityId,
          owner_id: ownerId,
          status: 'scheduled',
          ...payload,
        }).select('id').single()
        if (error) throw error
        meetingId = data.id
      }

      // Calendar invite (.ics) — best-effort AFTER the save. A send failure must
      // NOT roll back the save: it's caught + warned here and the mutation still
      // resolves so the modal closes. An edit re-send bumps SEQUENCE (see ics.ts)
      // so recipients' calendars update the event.
      if (sendInvite && attendeeList.length > 0) {
        try {
          const dealId = editing?.entity_id ?? entityId
          const isDeal = (editing?.entity_type ?? entityType) === 'deal' && dealId
          const link = `${window.location.origin}${isDeal ? `/app/deals/${dealId}` : '/app/sales-calendar'}`
          const ics = buildMeetingIcs(
            { id: meetingId, title: payload.title, start_time: startIso, end_time: endIso, location: payload.location, notes: payload.notes, attendee_emails: attendeeList },
            { organizerName: workspace?.name || 'Orkestra', organizerEmail: user?.email || 'invites@orkestrapro.com', link },
          )
          const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          const locHtml = payload.location
            ? (isUrl(payload.location) ? `<p><a href="${esc(payload.location)}">Join meeting</a></p>` : `<p>${esc(payload.location)}</p>`)
            : ''
          const html = `<p>${esc(workspace?.name || 'Orkestra')} invited you to a meeting:</p>` +
            `<p><strong>${esc(payload.title)}</strong></p>` +
            `<p>${format(new Date(startIso), 'EEE, MMM d, h:mm a')} &ndash; ${format(new Date(endIso), 'h:mm a')}</p>` +
            locHtml
          const { error: sendErr } = await sendEmail({
            to: attendeeList,
            subject: `Invitation: ${payload.title} — ${format(new Date(startIso), 'MMM d')}`,
            html,
            attachments: [{ filename: 'meeting.ics', content: icsToBase64(ics) }],
          })
          if (sendErr) throw sendErr
        } catch {
          toast.error('Meeting saved; invite failed to send')
        }
      }
    },
    onSuccess: () => { onSaved(); onClose() },
    onError: (e: any) => toast.error(e?.message || 'Failed to save meeting'),
  })

  const canSave = !!title.trim() && !!startLocal && !!endLocal && !save.isPending
  const attendeeCount = attendees.split(',').map(s => s.trim()).filter(Boolean).length

  // ─── Video meeting link (Meetings CP3) ────────────────────────────────────
  // Generating sets the slug in form state; it persists to sales_meetings on
  // Save (payload above). The slug becomes a public guest URL that the
  // meeting-guest-token fn validates. NOTE: privileged staff join (auto-name,
  // no join window) is deferred — for now staff use this same public link.
  const generateLink = () => {
    const slug = generateRoomSlug()
    setRoomToken(slug)
    const url = meetUrl(slug)
    if (!location.trim()) {
      setLocation(url)
    } else if (window.confirm('Replace the current location with the meeting link?')) {
      setLocation(url)
    }
    // else: keep both — token saved, location left untouched.
  }
  const copyLink = async () => {
    if (!roomToken) return
    try {
      await navigator.clipboard.writeText(meetUrl(roomToken))
      toast.success('Meeting link copied')
    } catch {
      toast.error('Could not copy the link')
    }
  }
  const removeLink = () => {
    if (!roomToken) return
    if (!window.confirm('Remove the video meeting link? Guests with the link will no longer be able to join.')) return
    const url = meetUrl(roomToken)
    setRoomToken(null)
    if (location.trim() === url && window.confirm('Also clear the location field?')) setLocation('')
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <motion.div initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }} transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 440, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 16, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{editing ? 'Edit meeting' : 'Schedule meeting'}</h2>
              <button type="button" onClick={onClose} aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'flex', padding: 2 }}>
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>
            <div>
              <label style={lbl}>Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} autoFocus placeholder="Discovery call…" style={{ ...field, fontWeight: 600 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={lbl}>Start</label>
                <input type="datetime-local" value={startLocal} onChange={e => setStartLocal(e.target.value)} style={{ ...field, colorScheme: 'dark' }} />
              </div>
              <div>
                <label style={lbl}>End</label>
                <input type="datetime-local" value={endLocal} onChange={e => setEndLocal(e.target.value)} style={{ ...field, colorScheme: 'dark' }} />
              </div>
            </div>
            <div>
              <label style={lbl}>Location or meeting link</label>
              <input value={location} onChange={e => setLocation(e.target.value)} placeholder="123 Main St, or https://meet…" style={field} />
            </div>
            <div>
              <label style={lbl}>Video meeting</label>
              {!roomToken ? (
                <button type="button" onClick={generateLink}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600 }}>
                  <Video style={{ width: 14, height: 14 }} /> Generate Orkestra meeting link
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 8 }}>
                  <Video style={{ width: 14, height: 14, color: 'var(--accent-text)', flexShrink: 0 }} />
                  <span title={meetUrl(roomToken)} style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--ink-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {`/meet/${roomToken}`}
                  </span>
                  <button type="button" onClick={copyLink} style={{ flexShrink: 0, padding: '4px 10px', background: 'var(--accent)', border: 'none', borderRadius: 6, cursor: 'pointer', color: 'var(--accent-ink)', fontSize: 11, fontWeight: 700 }}>Copy</button>
                  <button type="button" onClick={removeLink} style={{ flexShrink: 0, padding: '4px 8px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', display: 'inline-flex' }} aria-label="Remove meeting link">
                    <X style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              )}
            </div>
            <div>
              <label style={lbl}>Attendee emails (comma-separated)</label>
              <input value={attendees} onChange={e => setAttendees(e.target.value)} placeholder="jane@acme.com, sam@acme.com" style={field} />
            </div>
            <div>
              <label style={lbl}>Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Agenda, context…" style={{ ...field, resize: 'vertical' }} />
            </div>
            <div>
              <label style={lbl}>Reminder</label>
              <select value={reminderOpt} onChange={e => setReminderOpt(e.target.value)} style={{ ...field, cursor: 'pointer' }}>
                {reminderOpt === 'custom' && <option value="custom">Custom (keep existing)</option>}
                {REMINDER_OPTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            {attendeeCount > 0 && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: '2px 0' }}>
                <input type="checkbox" checked={sendInvite} onChange={e => setSendInvite(e.target.checked)}
                  style={{ width: 15, height: 15, marginTop: 1, flexShrink: 0, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                <span style={{ fontSize: 12, color: 'var(--ink-secondary)', lineHeight: 1.5 }}>
                  Send calendar invite to {attendeeCount} attendee{attendeeCount > 1 ? 's' : ''}
                  {editing && <span style={{ color: 'var(--ink-tertiary)' }}> — attendees will receive an updated invite</span>}
                </span>
              </label>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              <button type="button" onClick={onClose}
                style={{ padding: '8px 14px', background: 'var(--border-subtle)', border: 'none', borderRadius: 8, cursor: 'pointer', color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button type="button" onClick={() => save.mutate()} disabled={!canSave}
                style={{ padding: '8px 16px', background: 'var(--accent)', border: 'none', borderRadius: 8, cursor: canSave ? 'pointer' : 'not-allowed', color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, opacity: canSave ? 1 : 0.5 }}>
                {save.isPending ? 'Saving…' : editing ? 'Save' : 'Schedule'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
